import React, {useEffect, useMemo, useState} from "react";
import {IonImg, IonText, IonHeader, IonToolbar, IonTitle, IonPage, IonContent, IonSpinner} from "@ionic/react";
import {menuController} from "@ionic/core/components";
import {useStorefrontBranding} from "../../redux/branding";
import {hasCachedBranding} from "../../utils/branding";
import {useSystemStore} from "../../redux/system";
import {useAuthStore} from "../../redux/auth";
import PageLoading from "./PageLoading";

interface StorefrontPageProps {
    children: React.ReactNode;
    className?: string;
}

const FALLBACK_NAME = "Our Store";
// If site-config is slow/unreachable, reveal anyway after this long rather than
// leaving the visitor on the neutral splash indefinitely (graceful degradation).
const REVEAL_TIMEOUT_MS = 900;

/**
 * StorefrontPage — chrome-less page wrapper for the public storefront.
 *
 * - No side-menu button, no back button.
 * - Header shows tenant logo + name from branding store.
 * - Calls useStorefrontBranding() to trigger fetch and CSS variable injection.
 * - Disables the IonMenu on mount, re-enables on unmount.
 * - Avoids the default-theme colour flash: on a first visit (no cached brand)
 *   it shows a NEUTRAL, theme-aware splash (no brand tokens) until branding
 *   loads or the timeout fires. Returning visitors have brand tokens applied
 *   synchronously at bootstrap, so they skip the gate entirely.
 */
const StorefrontPage: React.FC<StorefrontPageProps> = ({children, className}) => {
    const {name, logoUrl, loaded} = useStorefrontBranding();
    const {isOnline} = useSystemStore();
    const {isLoggingOut} = useAuthStore();

    // Cache hit (checked once) => brand tokens already applied at bootstrap =>
    // reveal immediately, no neutral gate.
    const hadCachedBrand = useMemo(() => hasCachedBranding(), []);
    const [revealed, setRevealed] = useState(hadCachedBrand);

    // Suppress the slide-out menu on storefront routes.
    useEffect(() => {
        menuController.enable(false, "main").catch(() => {/* menu may not exist on web */});
        return () => {
            menuController.enable(true, "main").catch(() => {});
        };
    }, []);

    // Reveal once branding resolves, or after the timeout as a fallback.
    useEffect(() => {
        if (revealed) return;
        if (loaded) {
            setRevealed(true);
            return;
        }
        const timer = setTimeout(() => setRevealed(true), REVEAL_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [loaded, revealed]);

    // Neutral splash: uses --ion-background-color + medium (never brand tokens),
    // so there is no default-brand colour to flash before the real brand loads.
    if (!revealed) {
        return (
            <IonPage className={className}>
                <PageLoading showLoading={isLoggingOut} />
                <IonHeader className="app-header">
                    <IonToolbar>
                        <IonTitle>&nbsp;</IonTitle>
                    </IonToolbar>
                </IonHeader>
                <IonContent fullscreen>
                    <div style={{display: "flex", justifyContent: "center", alignItems: "center", height: "60vh"}}>
                        <IonSpinner name="crescent" color="medium" />
                    </div>
                </IonContent>
            </IonPage>
        );
    }

    const displayName = (loaded && name) ? name : FALLBACK_NAME;

    return (
        <IonPage className={className}>
            <PageLoading showLoading={isLoggingOut} />
            <IonHeader className="app-header">
                <IonToolbar className="app-header-toolbar app-header-toolbar-primary">
                    <IonTitle>
                        <div style={{display: "flex", alignItems: "center", justifyContent: "center", gap: "8px"}}>
                            {logoUrl && (
                                <IonImg
                                    src={logoUrl}
                                    alt={displayName}
                                    style={{height: "28px", width: "auto"}}
                                />
                            )}
                            <IonText>
                                <h4 className="ion-no-margin">{displayName}</h4>
                            </IonText>
                        </div>
                    </IonTitle>
                </IonToolbar>
            </IonHeader>
            <IonContent fullscreen>
                {!isOnline && (
                    <div className="network-warning-bar">You are currently offline.</div>
                )}
                {children}
            </IonContent>
        </IonPage>
    );
};

export default StorefrontPage;
